/**
 * Capture pose — where a scene is seen from, as arithmetic.
 *
 * An orbit pose (target, yaw, pitch, radius) is a pure function of a bounding
 * box: no render loop, no frame, nothing to await. That matters because the
 * headless paths this feeds (the bench's NullEngine, the MCP server) have no
 * render loop at all, and every past attempt to ask the engine where the camera
 * "ended up" wedged on a frame that never comes.
 *
 * Conventions, fixed here so every consumer agrees:
 *
 * - `yaw` is degrees of azimuth around +Y and IS the ArcRotateCamera `alpha` in
 *   degrees — no negation. TraceScene runs right-handed (`useRightHandedSystem
 *   = true`), so at alpha = +PI/2 the camera sits at world +Z with world +X on
 *   the screen right: the scene's front, and the angle the editor opens with.
 *   Both `focus_camera` and the sandbox's `e2e/bench-render.ts` were shipped once with this
 *   sign flipped, which mirrored every capture in X and made any left/right
 *   judgement read from them wrong. Keep the sign.
 * - `pitch` is degrees of polar angle measured FROM +Y and IS `beta` in degrees:
 *   0 = top-down, 90 = level with the horizon, 180 = from below.
 * - `radius` is the orbit distance in metres, `target` the world point orbited.
 * - `position` is derived, never authored: the camera's world position, so a
 *   consumer with a free camera (Blender, a future `PreviewPose`) does not have
 *   to re-derive the trigonometry and get the handedness wrong.
 */

export type Vec3 = [number, number, number];

export interface WorldBounds {
    min: Vec3;
    max: Vec3;
}

/** Where a scene is seen from. Bench metadata today; `PreviewPose` one day. */
export interface CapturePose {
    /** World point the camera orbits / looks at. */
    target: Vec3;
    /** Azimuth in degrees = ArcRotateCamera alpha (90 = camera at +Z, the front). */
    yaw: number;
    /** Polar angle in degrees from +Y = ArcRotateCamera beta (90 = level). */
    pitch: number;
    /** Orbit distance in metres. */
    radius: number;
    /** Derived camera world position — target + orbit offset. */
    position: Vec3;
    /** What authored it. */
    source: 'focus_camera' | 'blender' | 'default';
    /** Object that was framed, when a specific one was. */
    framed?: string;
}

/**
 * Default three-quarter view: yaw 126° = PI/2 + PI/5, the same off-dead-on
 * orbit the sandbox's `bench-render.ts` captures its hero shot from, and pitch 60° = PI/3,
 * the `iso` preset in `CAMERA_ANGLE_PRESETS`. A pose recorded without an
 * explicit angle lands here rather than wherever the camera happened to sit.
 */
export const DEFAULT_YAW = 126;
export const DEFAULT_PITCH = 60;

/** Padding applied to the bounds diagonal — matches focus_camera's framing. */
export const DEFAULT_RADIUS_SCALE = 1.5;

/** beta of exactly 0 or PI is a degenerate orbit basis; stay off the poles. */
const POLE_EPSILON_DEG = 0.06;

const DEG = Math.PI / 180;

const round = (n: number): number => Number(n.toFixed(5));
const roundVec = (v: Vec3): Vec3 => [round(v[0]), round(v[1]), round(v[2])];

export function boundsAreFinite(b: WorldBounds): boolean {
    return [...b.min, ...b.max].every(Number.isFinite)
        && b.max[0] >= b.min[0] && b.max[1] >= b.min[1] && b.max[2] >= b.min[2];
}

export function boundsCenter(b: WorldBounds): Vec3 {
    return [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2];
}

/** Diagonal length of the box — the size framing is computed from. */
export function boundsDiagonal(b: WorldBounds): number {
    const dx = b.max[0] - b.min[0], dy = b.max[1] - b.min[1], dz = b.max[2] - b.min[2];
    return Math.hypot(dx, dy, dz);
}

/** Wrap an azimuth into (-180, 180] so two equal directions compare equal. */
export function normalizeYaw(yawDeg: number): number {
    let y = yawDeg % 360;
    if (y <= -180) y += 360;
    if (y > 180) y -= 360;
    // -0 is a real hazard in JSON diffs and in tests.
    return y === 0 ? 0 : y;
}

/** Keep pitch inside the open interval (0, 180) so the orbit basis stays sane. */
export function clampPitch(pitchDeg: number): number {
    return Math.min(180 - POLE_EPSILON_DEG, Math.max(POLE_EPSILON_DEG, pitchDeg));
}

/**
 * Camera world position for an orbit pose.
 *
 * Babylon's ArcRotateCamera places the eye at
 * `(r·cos α·sin β, r·cos β, r·sin α·sin β)` relative to the target — the same
 * expression `scene-tools.ts` documents for the angle presets.
 */
export function orbitCameraPosition(target: Vec3, yawDeg: number, pitchDeg: number, radius: number): Vec3 {
    const a = yawDeg * DEG;
    const b = clampPitch(pitchDeg) * DEG;
    return roundVec([
        target[0] + radius * Math.cos(a) * Math.sin(b),
        target[1] + radius * Math.cos(b),
        target[2] + radius * Math.sin(a) * Math.sin(b),
    ]);
}

/** yaw/pitch in degrees → the ArcRotateCamera's alpha/beta in radians. */
export function poseToAlphaBeta(pose: Pick<CapturePose, 'yaw' | 'pitch'>): { alpha: number; beta: number } {
    return { alpha: pose.yaw * DEG, beta: clampPitch(pose.pitch) * DEG };
}

/** alpha/beta in radians → yaw/pitch in degrees. Inverse of poseToAlphaBeta. */
export function alphaBetaToYawPitch(alpha: number, beta: number): { yaw: number; pitch: number } {
    return { yaw: normalizeYaw(round(alpha / DEG)), pitch: round(clampPitch(beta / DEG)) };
}

/**
 * The orbit distance at which a box's eight corners reach 1/`margin` of a
 * perspective frame's half-width or half-height, seen from `alpha`/`beta`
 * (radians, about the box centre): 1.1 leaves about 9% of each half clear on
 * the tighter axis. `fov` is the vertical field of view, `aspect` width over height.
 */
export function fitOrbitRadius(bounds: WorldBounds, alpha: number, beta: number, fov: number, aspect: number, margin: number): number {
    const c = boundsCenter(bounds);
    const eye: Vec3 = [Math.cos(alpha) * Math.sin(beta), Math.cos(beta), Math.sin(alpha) * Math.sin(beta)];
    // Screen right is the orbit's alpha tangent, defined at the poles too; up completes the basis.
    const right: Vec3 = [-Math.sin(alpha), 0, Math.cos(alpha)];
    const up: Vec3 = [eye[1] * right[2] - eye[2] * right[1], eye[2] * right[0] - eye[0] * right[2], eye[0] * right[1] - eye[1] * right[0]];
    const tanV = Math.tan(fov / 2) / margin;
    const tanH = tanV * aspect;
    let radius = 0.01;
    for (const x of [bounds.min[0], bounds.max[0]]) for (const y of [bounds.min[1], bounds.max[1]]) for (const z of [bounds.min[2], bounds.max[2]]) {
        const p: Vec3 = [x - c[0], y - c[1], z - c[2]];
        const dot = (v: Vec3) => p[0] * v[0] + p[1] * v[1] + p[2] * v[2];
        // A corner toward the eye is nearer by dot(p, eye), so it needs that much more distance.
        const toward = dot(eye);
        radius = Math.max(radius, Math.abs(dot(right)) / tanH + toward, Math.abs(dot(up)) / tanV + toward);
    }
    return radius;
}

export interface OrbitPoseOptions {
    yaw?: number;
    pitch?: number;
    /** Multiplier on the bounds diagonal. Default 1.5. */
    radiusScale?: number;
    /** Instead of `radiusScale`, fit the box to a perspective frame from the pose's angles ({@link fitOrbitRadius}). */
    fit?: { fov: number; aspect: number; margin: number };
    source?: CapturePose['source'];
    framed?: string;
}

/**
 * An orbit pose that frames a bounding box — the headless equivalent of "press
 * F", computed rather than rendered.
 *
 * Degenerate bounds (an empty transform, a particle emitter, a single point)
 * would otherwise produce radius 0 and a camera inside the subject; they fall
 * back to a 1-metre box, the same way `SceneCamera.focusOnNode` does.
 */
export function orbitPoseFromBounds(bounds: WorldBounds, opts: OrbitPoseOptions = {}): CapturePose {
    const usable = boundsAreFinite(bounds) ? bounds : { min: [-0.5, -0.5, -0.5] as Vec3, max: [0.5, 0.5, 0.5] as Vec3 };
    const target = roundVec(boundsCenter(usable));
    const diag = boundsDiagonal(usable);
    const yaw = normalizeYaw(opts.yaw ?? DEFAULT_YAW);
    const pitch = clampPitch(opts.pitch ?? DEFAULT_PITCH);
    const radius = round(opts.fit
        ? fitOrbitRadius(usable, yaw * DEG, pitch * DEG, opts.fit.fov, opts.fit.aspect, opts.fit.margin)
        : Math.max(diag * (opts.radiusScale ?? DEFAULT_RADIUS_SCALE), 0.01));
    return {
        target,
        yaw,
        pitch,
        radius,
        position: orbitCameraPosition(target, yaw, pitch, radius),
        source: opts.source ?? 'default',
        ...(opts.framed ? { framed: opts.framed } : {}),
    };
}

/**
 * Pose from a free camera: where it is, and what it looks at.
 *
 * This is the direction the conversion runs for any instrument that authors a
 * camera rather than an orbit — Blender's, today. Radius is the eye-to-target
 * distance, so re-deriving the position from the pose reproduces the eye.
 */
export function poseFromLookAt(
    position: Vec3,
    target: Vec3,
    opts: { source?: CapturePose['source']; framed?: string } = {},
): CapturePose {
    const d: Vec3 = [position[0] - target[0], position[1] - target[1], position[2] - target[2]];
    const radius = Math.hypot(d[0], d[1], d[2]);
    if (radius < 1e-6) {
        // Eye on the target: there is no direction to recover. Frame the point.
        return orbitPoseFromBounds({ min: target, max: target }, opts);
    }
    const pitch = clampPitch(Math.acos(Math.max(-1, Math.min(1, d[1] / radius))) / DEG);
    const yaw = normalizeYaw(Math.atan2(d[2], d[0]) / DEG);
    const rounded = round(radius);
    return {
        target: roundVec(target),
        yaw: round(yaw),
        pitch: round(pitch),
        radius: rounded,
        position: orbitCameraPosition(roundVec(target), yaw, pitch, rounded),
        source: opts.source ?? 'default',
        ...(opts.framed ? { framed: opts.framed } : {}),
    };
}

/** One-line description for tool results and logs. */
export function describePose(pose: CapturePose): string {
    const t = pose.target.map(n => n.toFixed(2)).join(', ');
    return `target (${t}), yaw ${pose.yaw.toFixed(1)}°, pitch ${pose.pitch.toFixed(1)}°, radius ${pose.radius.toFixed(2)}m`;
}
